import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePractice } from '../contexts/PracticeContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';

// Admin section components
import { PracticeConfigSection } from '../components/admin/PracticeConfigSection';
import { AlertFilterSection } from '../components/admin/AlertFilterSection';
import { RoleBasedFilterSection } from '../components/admin/RoleBasedFilterSection';
import { SoundAlertSection } from '../components/admin/SoundAlertSection';
import { TeamSection } from '../components/admin/TeamSection';
import { CustomButtonsSection } from '../components/admin/CustomButtonsSection';
import { SmartwatchSection } from '../components/admin/SmartwatchSection';
import { DeviceManagementSection } from '../components/admin/DeviceManagementSection';
import { ReferralSection } from '../components/admin/ReferralSection';
import { DownloadsSection } from '../components/admin/DownloadsSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DENTAL_PULSE_LOGO = 'https://customer-assets.emergentagent.com/job_dentalpulse-4/artifacts/3espo6gq_DentalPulse_logoFinal%20%2B%20white.png';

export default function AdminPage() {
  const { user } = useAuth();
  const { practice, updateThresholds, updateAppearance, updatePracticeName, updateDisplaySettings, updateSoundSettings, fetchPractice } = usePractice();
  const { darkMode } = useTheme();
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [operatories, setOperatories] = useState([]);
  const [devices, setDevices] = useState([]);
  const [customButtons, setCustomButtons] = useState([]);
  const [smartwatchSeats, setSmartwatchSeats] = useState(null);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const [usersRes, operatoriesRes, devicesRes, buttonsRes, seatsRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/operatories`),
        axios.get(`${API}/devices`),
        axios.get(`${API}/practice/custom-buttons`),
        axios.get(`${API}/license/smartwatch-seats`).catch(() => ({ data: null }))
      ]);
      setUsers(usersRes.data);
      setOperatories(operatoriesRes.data);
      setDevices(devicesRes.data);
      setCustomButtons(buttonsRes.data || []);
      setSmartwatchSeats(seatsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  // Apply custom background from practice settings
  const bgStyle = darkMode ? {
    backgroundColor: '#0f172a',
    backgroundImage: practice?.background_image_dark ? `url(${practice.background_image_dark})` : 'none',
    backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed'
  } : {
    backgroundColor: practice?.background_color || '#f8fafc',
    backgroundImage: practice?.background_image ? `url(${practice.background_image})` : 'none',
    backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed'
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''}`} style={bgStyle}>
      <Sidebar />
      
      <div className="pl-28 pr-8 pt-6 pb-8">
        {/* Header */}
        <div className={`glass-panel rounded-2xl p-6 mb-6 flex items-center justify-between ${darkMode ? 'bg-slate-800/50' : ''}`}>
          <div>
            <h1 className={`text-3xl font-heading font-bold ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>Admin Settings</h1>
            <p className={`mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Manage your practice configuration</p>
          </div>
          <div className="flex-shrink-0 ml-4">
            <img src={DENTAL_PULSE_LOGO} alt="DentalPulse" className="h-12 w-auto object-contain" data-testid="dentalpulse-logo" />
          </div>
        </div>

        {/* Practice Configuration */}
        <PracticeConfigSection
          darkMode={darkMode}
          practice={practice}
          operatories={operatories}
          updateThresholds={updateThresholds}
          updateAppearance={updateAppearance}
          updatePracticeName={updatePracticeName}
          updateDisplaySettings={updateDisplaySettings}
          fetchPractice={fetchPractice}
          fetchData={fetchData}
        />

        {/* Alert Filters */}
        <AlertFilterSection
          darkMode={darkMode}
          practice={practice}
          updateDisplaySettings={updateDisplaySettings}
        />

        {/* Role Based Filtering */}
        <RoleBasedFilterSection
          darkMode={darkMode}
          practice={practice}
          customButtons={customButtons}
        />

        {/* Sound Alerts */}
        <SoundAlertSection
          darkMode={darkMode}
          practice={practice}
          updateSoundSettings={updateSoundSettings}
        />

        {/* Team Members */}
        <TeamSection
          darkMode={darkMode}
          users={users}
          operatories={operatories}
          currentUser={user}
          fetchData={fetchData}
          smartwatchSeats={smartwatchSeats}
        />

        {/* Custom Buttons */}
        <CustomButtonsSection
          darkMode={darkMode}
          customButtons={customButtons}
          fetchData={fetchData}
        />

        {/* Smartwatch Integration */}
        <SmartwatchSection
          darkMode={darkMode}
          practice={practice}
        />

        {/* Device Management */}
        <DeviceManagementSection
          darkMode={darkMode}
          devices={devices}
          operatories={operatories}
          fetchData={fetchData}
        />

        {/* Referral Program Section */}
        <ReferralSection darkMode={darkMode} />

        {/* Software Downloads Section */}
        <DownloadsSection darkMode={darkMode} />
      </div>
    </div>
  );
}
